// src/components/ProductEditPage.js
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  message,
  Upload,
  Tooltip,
  Switch,
  Typography,
  Card,
  Row,
  Col,
  Steps,
  Checkbox,
  Table,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckOutlined,
  PlusOutlined,
  ReloadOutlined,
  UserOutlined,
  TagOutlined,
  BarcodeOutlined,
  ShopOutlined,
  DatabaseOutlined,
  PictureOutlined,
  EditOutlined,
  LeftOutlined,
  RightOutlined,
  CloseOutlined, // <-- added
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const { Option } = Select;
const { Title, Text } = Typography;

const normalizeTypes = (raw) => {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {}
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return raw ? [String(raw)] : [];
};

const ProductEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
    // ✅ ADD THIS: Get user role
  const rawRole = localStorage.getItem("userRole") || "";
  const role = rawRole.toLowerCase();
  const isSuperAdmin = role === "super admin" || role === "super_admin";
  const isAdmin = role === "admin";
  
  // ✅ Show cost price fields only for super admin
  const showCostFields = isSuperAdmin;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const [images, setImages] = useState([]);
  const [description, setDescription] = useState("");

  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [fabrics, setFabrics] = useState([]);

  // colors
  const [allFabricColors, setAllFabricColors] = useState([]); // master list (label, value, fabricId)
  const [allowedFabricColors, setAllowedFabricColors] = useState([]); // filtered by selected fabrics

  const [isBedCategory, setIsBedCategory] = useState(false);
  const [selectedStorageTypes, setSelectedStorageTypes] = useState([]);
  const [pendingTypes, setPendingTypes] = useState(null);

  const [packagesPerType, setPackagesPerType] = useState({});
  const [singlePackageCount, setSinglePackageCount] = useState(1);
  const [sizeBasePrices, setSizeBasePrices] = useState({}); // {sizeId: basePrice}
  const [sizeBaseCosts, setSizeBaseCosts] = useState({});

  const [sizeDimensions, setSizeDimensions] = useState({}); // {sizeId: {length,width,height}}
  const [sizeStock, setSizeStock] = useState({}); // {sizeId: stock}
  const [originalSizeIds, setOriginalSizeIds] = useState([]);

  const fileInputRef = useRef(null);
  const [imageToReplace, setImageToReplace] = useState(null);

  const persistImageOrder = useCallback(
    async (orderedImages) => {
      const token = localStorage.getItem("token");

      try {
        await axiosInstance.post(
          "/admin/update-image-order",
          {
            productId: id,
            images: orderedImages
              .filter((img) => !String(img.id).startsWith("temp-"))
              .map((img) => ({
                id: img.id,
                order: img.order,
              })),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } catch (err) {
        console.error("Failed to persist image order", err);
        message.error("Image order could not be saved");
      }
    },
    [id]
  );
  /* ======================================================= */

  const hasSeenNonEmptySizesRef = useRef(false);

  const [uiStep, setUiStep] = useState(0);
  const steps = [
    { title: "Product Details", icon: <PictureOutlined /> },
    { title: "Types & Packaging", icon: <DatabaseOutlined /> },
    { title: "Fabrics & Colors", icon: <ShopOutlined /> },
    { title: "Variants & Pricing", icon: <TagOutlined /> },
  ];
  const responsiveSteps = steps.map((s) => ({
    ...s,
    title: typeof window !== "undefined" && window.innerWidth < 768 ? "" : s.title,
  }));
  const next = () => setUiStep((s) => Math.min(s + 1, steps.length - 1));
  const prev = () => setUiStep((s) => Math.max(s - 1, 0));

  const quillModules = useMemo(
    () => ({
      toolbar: [
        ["bold", "italic", "underline", "strike"],
        ["blockquote", "code-block"],
        [{ header: 1 }, { header: 2 }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        ["link", "image"],
        ["clean"],
      ],
    }),
    []
  );
  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      "code-block",
      "list",
      "bullet",
      "script",
      "link",
      "image",
    ],
    []
  );

  const mergeOptions = useCallback((selectedOptions, fetchedOptions) => {
    const merged = [...fetchedOptions];
    selectedOptions.forEach((sel) => {
      if (!merged.some((opt) => opt.value === sel.value)) merged.push(sel);
    });
    return merged;
  }, []);

  // read fabric increments
  const readFabricIncrement = (f) => {
    const keys = [
      "fabric_price",
      "price_increment",
      "increment",
      "extra_price",
      "price_euro",
      "price",
    ];
    for (const k of keys) {
      const n = Number(f?.[k]);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };
  const readFabricCost = (f) => {
    const keys = ["fabric_cost", "cost_increment", "cost_euro", "cost"];
    for (const k of keys) {
      const n = Number(f?.[k]);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };

  // API: fetch colors for fabricIds
 const fetchFabricColors = useCallback(async (fabricIds) => {
  try {
    if (!fabricIds?.length) return [];

    // ✅ MASTER list (includes newly added colors like Naples GREY)
    const res = await axiosInstance.get("/admin/fabrics-with-colors");

    // handle possible shapes
    const fabricsList = res.data?.fabrics || res.data || [];
    const wanted = new Set((fabricIds || []).map((x) => Number(x)));

    return (fabricsList || [])
      .filter((f) => wanted.has(Number(f.id)))
      .flatMap((f) =>
        (f.fabricColors || []).map((fc) => ({
          label: fc.color_name,
          value: Number(fc.id),
          image_url: fc.image_url,
          fabricId: Number(f.id),
        }))
      );
  } catch (e) {
    console.error("fetchFabricColors failed:", e);
    return [];
  }
}, []);


  const isNonOttoman = (s) => String(s || "").toLowerCase().replace(/[\s_-]+/g, "") === "nonottoman";

  // FIXED: Extract base prices from product for both bed and non-bed products
 const extractBasePricesFromProduct = useCallback((product, isBedCategory) => {
  const priceMap = {};
  const costMap = {};

  // 1) Use backend per-size preview if present
  for (const s of product?.Sizes || []) {
    const sid = Number(s.id);
    const p = Number(s.price_preview_default);
    if (Number.isFinite(p)) priceMap[sid] = p;

    // (optional if backend adds it later)
    const c = Number(s.cost_preview_default);
    if (Number.isFinite(c)) costMap[sid] = c;
  }

  // 2) Build per-size base from Variations (stable selection)
  const bestBySize = new Map();

  const isNonOtt = (st) =>
    String(st || "").toLowerCase().replace(/[\s_-]+/g, "") === "nonottoman";

  for (const v of product?.Variations || []) {
    const sid = Number(v.sizeId);
    if (!Number.isFinite(sid)) continue;

    const st = String(v.storage_type || "");
    // Beds: only Non Ottoman is the "base"
    // Non-beds: treat empty storage_type as base too
    const okStorage = isBedCategory ? isNonOtt(st) : (st.trim() === "" || isNonOtt(st));
    if (!okStorage) continue;

    const p = v.price_euro != null ? Number(v.price_euro) : null;
    const c = v.factory_price != null ? Number(v.factory_price) : null;
    const hasAny = Number.isFinite(p) || Number.isFinite(c);
    if (!hasAny) continue;

    const isBase = v.fabricId == null && v.fabricColorId == null; // prefer true if exists

    const prev = bestBySize.get(sid);
    if (!prev) {
      bestBySize.set(sid, { p, c, isBase });
      continue;
    }

    // Prefer base rows (fabric/color null)
    if (!prev.isBase && isBase) {
      bestBySize.set(sid, { p, c, isBase });
      continue;
    }

    // If both same "base-ness", prefer lower sale price (or fill missing)
    const prevP = prev.p;
    if (Number.isFinite(p)) {
      if (!Number.isFinite(prevP) || p < prevP) {
        bestBySize.set(sid, { p, c, isBase: prev.isBase || isBase });
      }
    } else if (!Number.isFinite(prevP) && Number.isFinite(c) && !Number.isFinite(prev.c)) {
      // no price available, at least fill cost
      bestBySize.set(sid, { p: prevP, c, isBase: prev.isBase || isBase });
    }
  }

  // Fill missing entries from bestBySize (don’t overwrite explicit preview_default)
  for (const [sid, row] of bestBySize.entries()) {
    if (priceMap[sid] == null && Number.isFinite(row.p)) priceMap[sid] = row.p;
    if (costMap[sid] == null && Number.isFinite(row.c)) costMap[sid] = row.c;
  }

  return { priceMap, costMap };
}, []);

  /** Prefill form and local state from product payload */
 const setFormData = useCallback(
  async (product) => {
    const productCategories =
      product.Categories?.map((cat) => ({
        value: Number(cat.id),
        label: cat.category_name,
      })) || [];

    const productSizes =
      product.Sizes?.map((size) => ({
        value: Number(size.id),
        label: size.size,
      })) || [];

    const productFabrics =
      product.Fabrics?.map((fabric) => ({
        value: Number(fabric.id),
        label: fabric.fabric_name,
        image_url: fabric.image_url,
        price_increment: readFabricIncrement(fabric),
        cost_increment: readFabricCost(fabric),
      })) || [];

    const bedCategory = productCategories.some((c) => c.label === "Beds");
    setIsBedCategory(bedCategory);

    if (bedCategory) {
      // ✅ Selected colors coming from product (prefill selection)
      const colorsFromEmbedded =
        (product.Fabrics || []).flatMap((f) =>
          (f.fabricColors || []).map((fc) => ({
            value: Number(fc.id),
            label: fc.color_name,
            image_url: fc.image_url,
            fabricId: Number(f.id),
          }))
        ) || [];

      // ✅ MASTER colors (fetch all colors for selected fabrics - includes new colors)
      const initialFabricIds = productFabrics.map((f) => f.value);
      const masterColors = await fetchFabricColors(initialFabricIds);

      // ✅ Merge master + embedded (embedded is fallback if anything missing)
      const mergedMap = new Map();
      (masterColors || []).forEach((c) => {
        mergedMap.set(`${Number(c.fabricId)}-${Number(c.value)}`, {
          ...c,
          fabricId: Number(c.fabricId),
          value: Number(c.value),
        });
      });
      (colorsFromEmbedded || []).forEach((c) => {
        mergedMap.set(`${Number(c.fabricId)}-${Number(c.value)}`, {
          ...c,
          fabricId: Number(c.fabricId),
          value: Number(c.value),
        });
      });

      const mergedColors = Array.from(mergedMap.values());

      // ✅ Keep a master list for selected fabrics
      setAllFabricColors(mergedColors);

      // ✅ Allowed = only those belonging to currently selected fabrics
      setAllowedFabricColors(
        mergedColors.filter((c) => initialFabricIds.includes(Number(c.fabricId)))
      );

      // ✅ Prefill selected color IDs exactly as product already has
      form.setFieldsValue({
        fabricColorIds: colorsFromEmbedded.map((c) => ({
          value: c.value,
          label: c.label,
        })),
      });
    } else {
      // Not a bed: keep fabrics if exist; only clear if truly empty
      if (productFabrics.length === 0) {
        setAllFabricColors([]);
        setAllowedFabricColors([]);
        form.setFieldsValue({ fabricIds: [], fabricColorIds: [] });
      }
    }

    // options
    setCategories((prev) => mergeOptions(productCategories, prev));
    setSizes((prev) => mergeOptions(productSizes, prev));
    setFabrics((prev) => mergeOptions(productFabrics, prev));

    setDescription(product.description || "");
    setOriginalSizeIds(productSizes.map((s) => s.value));

    // storage types
    const known = new Set(["Non Ottoman", "Ottoman", "Cloud Storage"]);
    const apiTypes = normalizeTypes(product.types);
    const fromPackages = Object.keys(product.packageCounts || {}).filter((k) => known.has(k));
    const prefillTypes = Array.from(new Set([...apiTypes, ...fromPackages]));
    if (bedCategory) setPendingTypes(prefillTypes);

    // packages - Use exactly what comes from backend
    const pkg = product.packageCounts || {};
    if (Object.keys(pkg).length > 0) {
      const next = {};
      Object.keys(pkg).forEach((t) => {
        next[t] = Number(pkg[t]) || 1;
      });
      setPackagesPerType(next);
      setSinglePackageCount(1); // reset
    } else {
      setPackagesPerType({});
      setSinglePackageCount(1);
    }

    // Base prices for Non Ottoman + Cost
    const { priceMap, costMap } = extractBasePricesFromProduct(product, bedCategory);
    setSizeBasePrices(priceMap);
    setSizeBaseCosts(costMap);

    // Per-size dimensions + stocks
    const dims = {};
    const stocks = {};
    (product.Sizes || []).forEach((s) => {
      const sid = Number(s.id);
      dims[sid] = {
        length: s.length ?? "",
        width: s.width ?? "",
        height: s.height ?? "",
      };
      const possibleStock = s.stock ?? s.stock_qty ?? s.quantity ?? s.size_stock ?? s.sizeQty ?? 0;
      stocks[sid] = Number(possibleStock) || 0;
    });
    setSizeDimensions(dims);
    setSizeStock(stocks);

    // Prefill other fields
    form.setFieldsValue({
      product_name: product.product_name || "",
      description: product.description || "",
      stock: Number(product.stock) || 0,
      price_euro: product.price_euro != null ? Number(product.price_euro) : 0,
      factory_price: product.factory_price != null ? Number(product.factory_price) : 0,
      sku: product.sku || "",
      shippingAvailable: product.shippingAvailable || false,
      weight: Number(product.weight) || 0,
      status: product.status || "in stock",

      categoryIds: productCategories.map((cat) => ({
        value: cat.value,
        label: cat.label,
        key: cat.value,
      })),

      sizeIds: productSizes,

      // ✅ Keep fabrics for both bed and non-bed (you already want this)
      fabricIds: productFabrics,
    });

    // images
    setImages(
      product.images
        ?.sort((a, b) => a.order - b.order)
        .map((image, index) => ({
          uid: image.id.toString(),
          name: image.image_url.split("/").pop(),
          status: "done",
          url: image.image_url,
          id: image.id,
          order: image.order ?? index,
        })) || []
    );
  },
  [form, mergeOptions, fetchFabricColors, extractBasePricesFromProduct]
);


  // Apply staged storage types after fields mount
  useEffect(() => {
    if (isBedCategory && pendingTypes) {
      form.setFieldsValue({ storageTypes: pendingTypes });
      setSelectedStorageTypes(Array.from(new Set(pendingTypes)));
      setPendingTypes(null);
    }
  }, [isBedCategory, pendingTypes, form]);

  // Keep packages keys aligned
  useEffect(() => {
    if (!isBedCategory) return;
    if (!selectedStorageTypes || selectedStorageTypes.length === 0) return;
    setPackagesPerType((prev) => {
      const next = { ...prev };
      (selectedStorageTypes || []).forEach((t) => {
        if (next[t] == null) next[t] = 1;
      });
      Object.keys(next).forEach((k) => {
        if (!selectedStorageTypes.includes(k)) delete next[k];
      });
      return next;
    });
  }, [selectedStorageTypes, isBedCategory]);

  // Load options then product
  const fetchOptions = useCallback(async () => {
    const [categoriesRes, sizesRes, fabricsRes] = await Promise.all([
      axiosInstance.get("/category/categories"),
      axiosInstance.get("/product/sizes"),
      axiosInstance.get("/admin/fabrics"),
    ]);

    const cats = (categoriesRes.data.categories || []).map((cat) => ({
      label: cat.category_name,
      value: Number(cat.id),
    })) || [];
    setCategories(cats);

    const sizeOpts = (sizesRes.data.sizes || []).map((size) => ({
      label: size.size,
      value: Number(size.id),
    })) || [];
    setSizes(sizeOpts);

    const fetchedFabrics = (fabricsRes.data.fabrics || []).map((fabric) => ({
      label: fabric.fabric_name,
      value: Number(fabric.id),
      image_url: fabric.image_url,
      price_increment: readFabricIncrement(fabric),
      cost_increment: readFabricCost(fabric),
    })) || [];
    setFabrics(fetchedFabrics);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await fetchOptions();
        const resp = await axiosInstance.get(`/product/${id}`);
        if (resp.data.success && resp.data.product) {
          await setFormData(resp.data.product);
        } else {
          message.error("Product not found.");
        }
      } catch {
        message.error("Failed to load product.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, fetchOptions, setFormData]);

  // watchers (top-level hooks)
  const selectedFabricsWatch = Form.useWatch("fabricIds", form) || [];
  const selectedFabricColorsWatch = Form.useWatch("fabricColorIds", form) || [];
  const selectedCategoriesWatch = Form.useWatch("categoryIds", form) || [];
  const storageTypesWatch = Form.useWatch("storageTypes", form) || [];

  // sync storage types from watch → state (for check icons)
  useEffect(() => {
    const next = storageTypesWatch || [];
    setSelectedStorageTypes((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, [JSON.stringify(storageTypesWatch)]);

  // Beds toggle
  useEffect(() => {
    const bed = (selectedCategoriesWatch || []).some((c) => c.label === "Beds");
    if (bed !== isBedCategory) setIsBedCategory(bed);
    if (!bed && !loading) {
  if (selectedStorageTypes.length) setSelectedStorageTypes([]);
  const current = form.getFieldValue("storageTypes") || [];
  if (current.length) form.setFieldsValue({ storageTypes: [] });

  // Don't automatically clear fabrics for non-bed products
  // Only clear if user hasn't selected any fabrics yet
  const currentFabrics = form.getFieldValue("fabricIds") || [];
  if (currentFabrics.length === 0) {
    form.setFieldsValue({ fabricIds: [], fabricColorIds: [] });
    setAllowedFabricColors([]);
    setAllFabricColors([]);
  }
}
  }, [isBedCategory, loading, selectedStorageTypes.length, form, JSON.stringify(selectedCategoriesWatch)]);

  // Handle fabric change: fetch colors; if API colors have no fabricId, infer
  const handleFabricChange = useCallback(
    async (selectedFabrics) => {
      const fabricIds = (selectedFabrics || []).map((f) => f.value);
      if (!fabricIds.length) {
        setAllowedFabricColors([]);
        form.setFieldsValue({ fabricColorIds: [] });
        return;
      }

      // try API
      const fetched = await fetchFabricColors(fabricIds);

      let nextColors = [];
      if (fetched.length) {
        // if any lack fabricId, try infer from existing embedded list
        const embeddedMap = new Map();
        allFabricColors.forEach((c) => embeddedMap.set(`${c.value}-${c.fabricId}`, c));

        nextColors = fetched.map((c) => {
          if (c.fabricId != null) return c;
          // infer: if only one fabric selected, assign it
          if (fabricIds.length === 1) return { ...c, fabricId: fabricIds[0] };
          // multi-select: try find a match from embedded by color id
          const found = Array.from(embeddedMap.values()).find((e) => e.value === c.value && fabricIds.includes(e.fabricId));
          return found ? found : c; // if still no fabricId, it will be filtered out per-fabric
        });
      }

      // If fetched is empty or useless, fall back to embedded master (already has fabricId)
      if (!nextColors.length) {
        nextColors = allFabricColors.filter((c) => fabricIds.includes(Number(c.fabricId)));
      }

      // Limit allowed to the selected fabrics
      const allowed = nextColors.filter((c) => fabricIds.includes(Number(c.fabricId)));
      setAllowedFabricColors(allowed);

      // prune current selections to allowed
      const current = form.getFieldValue("fabricColorIds") || [];
      const filtered = current.filter((c) => allowed.some((a) => a.value === c.value));
      form.setFieldsValue({ fabricColorIds: filtered });
    },
    [fetchFabricColors, form, allFabricColors]
  );

  // When selected fabrics array changes externally (prefill), align allowed list
  useEffect(() => {
    const fabricIds = (selectedFabricsWatch || []).map((f) => f.value);
    if (!fabricIds.length) {
      setAllowedFabricColors([]);
      return;
    }
    const allowed = (allFabricColors || []).filter((c) => fabricIds.includes(Number(c.fabricId)));
    setAllowedFabricColors(allowed);
  }, [JSON.stringify(selectedFabricsWatch), allFabricColors]);

  // Keep sizeDimensions and sizeStock keys aligned with current sizeIds
  useEffect(() => {
    const chosen = form.getFieldValue("sizeIds") || [];
    if (!Array.isArray(chosen)) return;
    const picked = chosen.map((x) => Number(x.value));
    setSizeDimensions((prev) => {
      const next = { ...prev };
      picked.forEach((sid) => {
        if (!next[sid]) next[sid] = { length: "", width: "", height: "" };
      });
      if (hasSeenNonEmptySizesRef.current) {
        Object.keys(next).forEach((k) => {
          const sid = Number(k);
          if (!picked.includes(sid)) delete next[sid];
        });
      }
      return next;
    });

    setSizeStock((prev) => {
      const next = { ...prev };
      picked.forEach((sid) => {
        if (next[sid] == null) next[sid] = 0;
      });
      if (hasSeenNonEmptySizesRef.current) {
        Object.keys(next).forEach((k) => {
          const sid = Number(k);
          if (!picked.includes(sid)) delete next[sid];
        });
      }
      return next;
    });

    if (picked.length > 0) hasSeenNonEmptySizesRef.current = true;
  }, [form]);

  /** Save */
    /** Save */
const handleUpdate = useCallback(
  async (values) => {
    try {
      setUpdating(true);
      const token = localStorage.getItem("token");

      let types = [];
      if (isBedCategory) {
        types = form.getFieldValue("storageTypes") || [];
      } else {
        types = (values.type || "").split(",").map((x) => x.trim()).filter(Boolean);
      }

      let packageCounts = null;
      if (isBedCategory) {
        const pc = {};
        (selectedStorageTypes || []).forEach((t) => {
          const n = Number(packagesPerType?.[t]);
          if (Number.isInteger(n) && n >= 1 && n <= 50) pc[t] = n;
        });
        packageCounts = Object.keys(pc).length ? pc : null;
      } else {
        const n = Number(singlePackageCount);
        if (Number.isInteger(n) && n >= 1 && n <= 50) packageCounts = { "Non Ottoman": n };
      }

      const priceMap = {};
      Object.entries(sizeBasePrices).forEach(([sid, val]) => {
        const n = Number(val);
        if (Number.isFinite(n)) priceMap[Number(sid)] = n;
      });
      
      const costMap = {};
      Object.entries(sizeBaseCosts).forEach(([sid, val]) => {
        const n = Number(val);
        if (Number.isFinite(n)) costMap[Number(sid)] = n;
      });

      // preserve original sizes if user did NOT touch size field
      const currentSizeIds =
        values.sizeIds && values.sizeIds.length ? values.sizeIds.map((x) => x.value) : originalSizeIds;

      // only calculate removed sizes when sizes actually changed
      const removedSizeIds =
        values.sizeIds && values.sizeIds.length ? originalSizeIds.filter((oid) => !currentSizeIds.includes(oid)) : [];

      // Build size dimensions safely
      const sizeDimensionsPayload = {};
      currentSizeIds.forEach((sid) => {
        const d = sizeDimensions[Number(sid)] || {};
        sizeDimensionsPayload[sid] = {
          length: (d.length ?? "").toString().trim() || null,
          width: (d.width ?? "").toString().trim() || null,
          height: (d.height ?? "").toString().trim() || null,
        };
      });

      // Build per-size stock map (source: local state sizeStock)
      const sizeStockPayload = {};
      currentSizeIds.forEach((sid) => {
        const raw = sizeStock[Number(sid)];
        const n = raw == null ? 0 : Number(raw);
        sizeStockPayload[sid] = Number.isFinite(n) ? n : 0;
      });

      // Build sizes array (what backend expects when quick-edit sends 'sizes')
      const sizesPayload = currentSizeIds.map((sid) => ({
        sizeId: Number(sid),
        stock: sizeStockPayload[sid] ?? 0,
        length: (sizeDimensionsPayload[sid]?.length ?? null),
        width: (sizeDimensionsPayload[sid]?.width ?? null),
        height: (sizeDimensionsPayload[sid]?.height ?? null),
      }));

      // If sizes exist, compute totalStock from them; otherwise use top-level stock input
      const stockValue = form.getFieldValue("stock");
      const totalStockFromSizes =
        sizesPayload.length > 0 ? sizesPayload.reduce((sum, r) => sum + (Number(r.stock) || 0), 0) : Number(stockValue ?? 0);
      const finalStockValue = Number.isFinite(Number(totalStockFromSizes)) ? Number(totalStockFromSizes) : 0;

      // Get categoryIds properly from form
      const categoryIdsFromForm = form.getFieldValue("categoryIds") || [];
      const categoryIdsToUse = Array.isArray(categoryIdsFromForm) ? categoryIdsFromForm : [];

      // Always include fabric fields if they exist in form values
      const fabricIdsValue = form.getFieldValue("fabricIds");
      const fabricColorIdsValue = form.getFieldValue("fabricColorIds");

      // 🟢 NEW: Create fabric price increments maps
      const fabricPriceIncrements = {};
      const fabricCostIncrements = {};
      
      if (fabricIdsValue && Array.isArray(fabricIdsValue) && fabricIdsValue.length > 0) {
        fabricIdsValue.forEach((fabricObj) => {
          const fabricId = fabricObj.value;
          const fabric = fabrics.find(f => f.value === fabricId);
          if (fabric) {
            fabricPriceIncrements[fabricId] = Number(fabric.price_increment) || 0;
            fabricCostIncrements[fabricId] = Number(fabric.cost_increment) || 0;
          }
        });
      }

      // FINAL PAYLOAD (safe)
      const payload = {
        product_name: values.product_name,
        description,
        type: types.length ? types[0] : "",
        types,
        // set stock to the computed value (server will prioritize incomingSizes if present)
        stock: finalStockValue,
        price_euro: values.price_euro != null ? Number(values.price_euro) : 0,
        factory_price: values.factory_price != null ? Number(values.factory_price) : 0,
        sku: values.sku,
        shippingAvailable: Boolean(values.shippingAvailable),
        weight: Number(values.weight || 0),
        status: values.status,
        categoryIds: categoryIdsToUse.map((x) => {
          if (x && typeof x === 'object' && x.value !== undefined) {
            return x.value;
          }
          return Number(x) || x;
        }).filter(id => id != null),

        // DO NOT send empty sizeIds
        ...(currentSizeIds.length ? { sizeIds: currentSizeIds } : {}),

        existingImages: images
          .filter((img) => !String(img.id).startsWith("temp-"))
          .sort((a, b) => a.order - b.order)
          .map((img, index) => ({
            id: img.id,
            order: index, // THIS SAVES ORDER
          })),

        packageCounts,

        // only send when exists
        priceMap: Object.keys(priceMap).length ? priceMap : undefined,
        costPriceMap: Object.keys(costMap).length ? costMap : undefined,

        // 🟢 NEW: Send fabric increments to backend
        ...(Object.keys(fabricPriceIncrements).length > 0 && { fabricPriceIncrements }),
        ...(Object.keys(fabricCostIncrements).length > 0 && { fabricCostIncrements }),

        // only send when sizes actually removed
        removedSizeIds: removedSizeIds.length ? removedSizeIds : undefined,

        // Keep dimensions (this remains useful)
        sizeDimensions: Object.keys(sizeDimensionsPayload).length ? sizeDimensionsPayload : undefined,

        // IMPORTANT: send 'sizes' array (backend will upsert ProductSize rows and compute final product.stock)
        ...(sizesPayload.length ? { sizes: sizesPayload } : {}),
      };

      // Add fabric fields to payload
      if (fabricIdsValue && Array.isArray(fabricIdsValue) && fabricIdsValue.length > 0) {
        payload.fabricIds = fabricIdsValue.map((x) => x.value);
      }

      if (fabricColorIdsValue && Array.isArray(fabricColorIdsValue) && fabricColorIdsValue.length > 0) {
        payload.fabricColorIds = fabricColorIdsValue.map((x) => x.value);
      }

      console.log("DEBUG - Payload being sent:", {
        categoryIds: payload.categoryIds,
        fabricIds: payload.fabricIds,
        fabricColorIds: payload.fabricColorIds,
        fabricPriceIncrements: payload.fabricPriceIncrements,
        fabricCostIncrements: payload.fabricCostIncrements,
        categoryIdsRaw: form.getFieldValue("categoryIds"),
        fabricIdsRaw: form.getFieldValue("fabricIds"),
        isBedCategory: isBedCategory,
        payloadKeys: Object.keys(payload)
      });

      const response = await axiosInstance.put(`/admin/product/${id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.data.success) {
        message.error("Update failed.");
        return;
      }

      // New images
      const newImages = images.filter((img) => String(img.id).startsWith("temp-"));
      const replacedImages = images.filter((img) => !String(img.id).startsWith("temp-") && img.originFileObj);

      if (newImages.length > 0) {
        const fd = new FormData();
        fd.append("productId", id);
        newImages.forEach((img) => fd.append("images", img.originFileObj));
        try {
          await axiosInstance.post("/admin/product/images", fd, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          });
        } catch {
          message.error("Product saved, but some images failed to upload.");
        }
      }

      if (replacedImages.length > 0) {
        await Promise.all(
          replacedImages.map(async (img) => {
            const fd = new FormData();
            fd.append("productId", id);
            fd.append("imageId", img.id);
            fd.append("images", img.originFileObj);
            try {
              await axiosInstance.post("/admin/update_images", fd, {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${token}`,
                },
              });
            } catch {}
          })
        );
      }

      message.success("Product updated successfully!");
      navigate("/products");
    } catch (error) {
      console.error("Update error:", error);
      message.error("Error updating product.");
    } finally {
      setUpdating(false);
    }
  },
  [
    id,
    form,
    description,
    images,
    isBedCategory,
    selectedStorageTypes,
    packagesPerType,
    singlePackageCount,
    sizeBasePrices,
    sizeBaseCosts,
    originalSizeIds,
    sizeDimensions,
    sizeStock,
    navigate,
    fabrics,
  ]
);

  const triggerReplaceFile = useCallback((imageId) => {
    setImageToReplace(imageId);
    fileInputRef.current?.click();
  }, []);
  const handleReplaceFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        message.error("You can only upload image files!");
        return;
      }
      setImages((prev) =>
        prev.map((img) => (img.id === imageToReplace ? { ...img, originFileObj: file, url: URL.createObjectURL(file) } : img))
      );
      e.target.value = "";
    },
    [imageToReplace]
  );
  const handleImageUpload = useCallback((file) => {
    if (!file.type.startsWith("image/")) {
      message.error("You can only upload image files!");
      return false;
    }
    setImages((prev) => [
      ...prev,
      {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: URL.createObjectURL(file),
        id: `temp-${Date.now()}`,
        originFileObj: file,
        order: prev.length, // ✅ ADD THIS
      },
    ]);
    return false;
  }, []);

  // ======== NEW: remove image handler ========
  const handleRemoveImage = useCallback(
    (imageId) => {
      setImages((prev) => {
        const reordered = prev.filter((img) => img.id !== imageId).map((img, index) => ({ ...img, order: index }));

        // ✅ SAVE ORDER TO BACKEND
        persistImageOrder(reordered);

        return reordered;
      });
    },
    [persistImageOrder]
  );

  const moveImage = useCallback(
    (imageId, direction) => {
      setImages((prev) => {
        const index = prev.findIndex((img) => img.id === imageId);
        if (index === -1) return prev;

        const newIndex = direction === "left" ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= prev.length) return prev;

        const updated = [...prev];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

        const reordered = updated.map((img, i) => ({ ...img, order: i }));

        // ✅ SAVE ORDER TO BACKEND
        persistImageOrder(reordered);

        return reordered;
      });
    },
    [persistImageOrder]
  );

  // =================================================

  // ======== helpers for fabric colors UI ========
  const selectedColorIdSet = useMemo(() => {
    const arr = selectedFabricColorsWatch || [];
    return new Set(arr.map((x) => x.value));
  }, [JSON.stringify(selectedFabricColorsWatch)]);

  const colorsByFabric = useMemo(() => {
    const map = {};
    (allowedFabricColors || []).forEach((c) => {
      const fid = Number(c.fabricId);
      if (!map[fid]) map[fid] = [];
      map[fid].push(c);
    });
    return map;
  }, [allowedFabricColors]);

  const setFabricColorsFor = useCallback(
    (fabricId, colorIds) => {
      const current = (form.getFieldValue("fabricColorIds") || []).map((x) => x.value);
      const keep = (allowedFabricColors || []).filter((c) => Number(c.fabricId) !== Number(fabricId)).map((c) => c.value);
      const nextSet = new Set([...current.filter((id) => keep.includes(id)), ...colorIds]);
      const next = Array.from(nextSet).map((id) => {
        const c = allowedFabricColors.find((x) => x.value === id);
        return { value: id, label: c?.label ?? String(id) };
      });
      form.setFieldsValue({ fabricColorIds: next });
    },
    [allowedFabricColors, form]
  );

  const handleSelectAllColors = useCallback((fabricId) => {
    const ids = (colorsByFabric[fabricId] || []).map((c) => c.value);
    setFabricColorsFor(fabricId, ids);
  }, [colorsByFabric, setFabricColorsFor]);
  const handleClearColors = useCallback((fabricId) => setFabricColorsFor(fabricId, []), [setFabricColorsFor]);
  const toggleColor = useCallback(
    (colorId, checked) => {
      const current = (form.getFieldValue("fabricColorIds") || []).map((x) => x.value);
      const nextSet = new Set(current);
      if (checked) nextSet.add(colorId);
      else nextSet.delete(colorId);
      const next = Array.from(nextSet).map((id) => {
        const c = allowedFabricColors.find((x) => x.value === id);
        return { value: id, label: c?.label ?? String(id) };
      });
      form.setFieldsValue({ fabricColorIds: next });
    },
    [allowedFabricColors, form]
  );

  const fabricPrice = useCallback(
    (fabricId) => {
      const f = fabrics.find((x) => x.value === fabricId);
      const inc = Number(f?.price_increment ?? 0);
      return isFinite(inc) ? inc : 0;
    },
    [fabrics]
  );
  const fabricCost = useCallback(
    (fabricId) => {
      const f = fabrics.find((x) => x.value === fabricId);
      const inc = Number(f?.cost_increment ?? 0);
      return isFinite(inc) ? inc : 0;
    },
    [fabrics]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600 font-medium">Loading product…</p>
        </div>
      </div>
    );
  }

  const multiSelectCommon = {
    mode: "multiple",
    showSearch: true,
    optionFilterProp: "label",
    maxTagCount: "responsive",
    allowClear: true,
    className: "w-full rounded-xl",
    popupClassName: "rounded-xl",
  };

  // ---- Size Pricing & Dimensions table (always read from form to avoid race) ----
  const renderSizePricingAndDimensions = () => {
    const chosen = form.getFieldValue("sizeIds") || [];
    if (!Array.isArray(chosen) || chosen.length === 0) return null;

    const ds = chosen.map((s) => ({
      key: Number(s.value),
      sid: Number(s.value),
      sizeLabel: s.label,
    }));

    const columns = [
      {
        title: "SIZE",
        dataIndex: "sizeLabel",
        key: "sizeLabel",
        width: 180,
        render: (txt) => <span className="font-medium text-gray-700">{txt}</span>,
      },
        // ✅ Conditionally show COST column only for super admin
  ...(showCostFields ? [{
    title: "COST (£)",
    dataIndex: "sid",
    key: "cost",
    width: 180,
    render: (sid) => (
      <InputNumber
        min={0}
        step={0.01}
        value={sizeBaseCosts?.[sid] === undefined || sizeBaseCosts?.[sid] === null ? undefined : Number(sizeBaseCosts?.[sid])}
        onChange={(v) =>
          setSizeBaseCosts((prev) => ({
            ...prev,
            [sid]: v == null ? undefined : Number(v),
          }))
        }
        className="w-full"
        placeholder="Enter cost"
      />
    ),
  }] : []),
      {
        title: "PRICE (€)",
        dataIndex: "sid",
        key: "price",
        width: 180,
        render: (sid) => (
          <InputNumber
            min={0}
            step={0.01}
            value={sizeBasePrices?.[sid] === undefined || sizeBasePrices?.[sid] === null ? undefined : Number(sizeBasePrices?.[sid])}
            onChange={(v) =>
              setSizeBasePrices((prev) => ({
                ...prev,
                [sid]: v == null ? undefined : Number(v),
              }))
            }
            className="w-full"
            placeholder="Enter price"
          />
        ),
      },
      // STOCK column (per-size)
      {
        title: "STOCK",
        dataIndex: "sid",
        key: "stock",
        width: 140,
        render: (sid) => (
          <InputNumber
            min={0}
            step={1}
            value={sizeStock?.[sid] === undefined ? undefined : Number(sizeStock?.[sid])}
            onChange={(v) => setSizeStock((prev) => ({ ...prev, [sid]: v == null ? 0 : Number(v) }))}
            className="w-full"
            placeholder="Qty"
          />
        ),
      },
      ...(isBedCategory
        ? [
            {
              title: "BASE PRICE (NON OTTOMAN)",
              dataIndex: "sid",
              key: "basePrice",
              width: 200,
              render: (sid) => (
                <InputNumber
                  min={0}
                  step={0.01}
                  value={sizeBasePrices?.[sid] === undefined || sizeBasePrices?.[sid] === null ? undefined : Number(sizeBasePrices?.[sid])}
                  onChange={(v) =>
                    setSizeBasePrices((prev) => ({
                      ...prev,
                      [sid]: v == null ? undefined : Number(v),
                    }))
                  }
                  className="w-full"
                  placeholder="0.00"
                />
              ),
            },
          ]
        : []),
      {
        title: "LENGTH",
        dataIndex: "sid",
        key: "length",
        width: 140,
        render: (sid) => (
          <Input
            value={String((sizeDimensions?.[sid]?.length ?? "")).toString()}
            onChange={(e) =>
              setSizeDimensions((prev) => ({
                ...prev,
                [sid]: { ...(prev?.[sid] || {}), length: e.target.value },
              }))
            }
            placeholder="e.g., 120"
          />
        ),
      },
      {
        title: "WIDTH",
        dataIndex: "sid",
        key: "width",
        width: 140,
        render: (sid) => (
          <Input
            value={String((sizeDimensions?.[sid]?.width ?? "")).toString()}
            onChange={(e) =>
              setSizeDimensions((prev) => ({
                ...prev,
                [sid]: { ...(prev?.[sid] || {}), width: e.target.value },
              }))
            }
            placeholder="e.g., 120"
          />
        ),
      },
      {
        title: "HEIGHT",
        dataIndex: "sid",
        key: "height",
        width: 140,
        render: (sid) => (
          <Input
            value={String((sizeDimensions?.[sid]?.height ?? "")).toString()}
            onChange={(e) =>
              setSizeDimensions((prev) => ({
                ...prev,
                [sid]: { ...(prev?.[sid] || {}), height: e.target.value },
              }))
            }
            placeholder="e.g., 120"
          />
        ),
      },
    ];

    return (
      <div className="bg-blue-50 p-6 rounded-2xl border border-blue-200 mb-6">
        <Title level={4} className="text-blue-900 mb-4 flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <TagOutlined className="text-blue-500" />
          Size Pricing & Dimensions
        </Title>

        <Table dataSource={ds} columns={columns} pagination={false} bordered size="middle" className="bg-white rounded-xl overflow-hidden" />

        <div className="text-xs text-blue-700 mt-3 bg-blue-100 p-3 rounded-lg">
          {isBedCategory ? (
            <>
              Prices are for <strong>Non Ottoman</strong>. Ottoman/Cloud are derived server-side. Dimensions (Length, Width, Height) are saved on the Product-Size relation.
            </>
          ) : (
            <>Dimensions (Length, Width, Height) are saved on the Product-Size relation.</>
          )}
        </div>
      </div>
    );
  };

  // ---- Package Configuration Section ----
  const renderPackageConfiguration = () => {
    return (
      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
        <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <DatabaseOutlined className="text-green-500" />
          Package Configuration
        </Title>

        {/* ✅ FIXED: Unified rendering logic for both bed & non-bed */}
        {Object.keys(packagesPerType || {}).length > 1 ? (
          <>
            <div className="font-semibold mb-4 text-gray-700">Packages per unit (by type)</div>
            {Object.keys(packagesPerType).map((t) => (
              <div key={t} className="flex items-center gap-4 mb-3 bg-white p-4 rounded-xl border border-gray-200">
                <div className="w-40 font-medium text-gray-700">{t}</div>
                <InputNumber
                  min={1}
                  max={50}
                  value={packagesPerType[t]}
                  onChange={(v) => setPackagesPerType((p) => ({ ...p, [t]: v == null ? 1 : Number(v) }))}
                  className="h-11 rounded-lg"
                />
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="font-semibold mb-3 text-gray-700">Packages per unit</div>
            <InputNumber
              min={1}
              max={50}
              value={Object.values(packagesPerType || {})[0] ?? singlePackageCount ?? 1}
              onChange={(v) => {
                if (Object.keys(packagesPerType || {}).length > 0) {
                  const firstKey = Object.keys(packagesPerType)[0];
                  setPackagesPerType((p) => ({ ...p, [firstKey]: v == null ? 1 : Number(v) }));
                } else {
                  setSinglePackageCount(v == null ? 1 : Number(v));
                }
              }}
              className="h-11 rounded-lg"
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 lg:p-6">
      <div className="max-w-6xl mx-auto">
        <Card className="border-0 shadow-lg rounded-3xl bg-white overflow-hidden">
          <div className="p-6 lg:p-8">
            {/* Back */}
            <div className="flex items-center justify-between mb-6">
              <Button type="text" icon={<LeftOutlined />} onClick={() => navigate("/products")}>
                Back
              </Button>
            </div>

            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-50 rounded-3xl mb-4">
                <EditOutlined className="text-3xl text-blue-600" />
              </div>
              <Title level={1} className="!mb-2 text-gray-900">
                Edit Product
              </Title>
              <Text className="text-gray-600 text-lg">Update product details and configurations</Text>
            </div>

            {/* Steps */}
            <div className="mb-8">
              <Steps current={uiStep} items={responsiveSteps} size="small" responsive={false} />
            </div>

            <Form form={form} layout="vertical" onFinish={handleUpdate} className="space-y-8">
              {/* STEP 0 */}
              {uiStep === 0 && (
                <div className="animate-fade-in">
                  <Row gutter={[24, 24]} align="top">
                    <Col xs={24} md={14}>
                      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                        <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <UserOutlined className="text-blue-500" />
                          Basic Information
                        </Title>

                        <Row gutter={[16, 8]}>
                          <Col xs={24}>
                            <Form.Item label="Product Name" name="product_name" rules={[{ required: true, message: "Product Name is required" }]}>
                              <Input className="h-12 rounded-xl border-gray-300 hover:border-blue-500 focus:border-blue-500" placeholder="Enter product name" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Form.Item label="Description" name="description" className="mb-0" rules={[{ required: true, message: "Description is required" }]}>
                          <div className="quill-block rounded-xl border border-gray-300 hover:border-blue-500 focus-within:border-blue-500 transition-colors duration-200 overflow-hidden">
                            <ReactQuill theme="snow" value={description} onChange={setDescription} modules={quillModules} formats={quillFormats} />
                          </div>
                        </Form.Item>

                        <Row gutter={[16, 8]} className="mt-4">
                          <Col xs={24} md={12}>
                            <Form.Item label={isBedCategory ? "Storage Types" : "Type(s)"} name={isBedCategory ? "storageTypes" : "type"} rules={[{ required: true, message: "Please select at least one type" }]}>
                              {isBedCategory ? (
                                <Select
                                  mode="multiple"
                                  placeholder="Select storage types"
                                  onChange={(vals) => form.setFieldsValue({ storageTypes: vals })}
                                  className="w-full h-12 rounded-xl"
                                  showSearch
                                  optionFilterProp="label"
                                  maxTagCount="responsive"
                                  allowClear
                                  options={[
                                    { value: "Non Ottoman", label: "Non Ottoman" },
                                    { value: "Ottoman", label: "Ottoman" },
                                    { value: "Cloud Storage", label: "Cloud Storage" },
                                  ]}
                                  popupClassName="storage-types-dd"
                                  optionRender={(opt) => {
                                    const selected = (selectedStorageTypes || []).includes(opt.value);
                                    return (
                                      <div className="flex w-full items-center justify-between">
                                        <span>{opt.label}</span>
                                        {selected ? <CheckOutlined className="text-green-600" /> : null}
                                      </div>
                                    );
                                  }}
                                />
                              ) : (
                                <Input placeholder="e.g., Sofa, Chair, etc." className="h-12 rounded-xl border-gray-300 hover:border-blue-500 focus:border-blue-500" />
                              )}
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label="Stock" name="stock">
                              <InputNumber min={0} className="w-full h-12 rounded-xl" placeholder="Enter stock quantity" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    {/* RIGHT: Images */}
                    <Col xs={24} md={10}>
                      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                        <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          <PictureOutlined className="text-purple-500" />
                          Product Images
                        </Title>
                        <div className="flex flex-wrap gap-4">
                          {images.map((image, index) => (
                            <div key={image.id} className="relative text-center group">
                              <img src={image.url} alt="Product" className="w-24 h-24 rounded-xl transition-all duration-300 group-hover:scale-105 object-cover shadow-sm" />

                              {/* MOVE LEFT */}
                              {index > 0 && (
                                <Tooltip title="Move Left">
                                  <Button
                                    size="small"
                                    shape="circle"
                                    className="!absolute !top-1/2 !-translate-y-1/2 !left-1 !opacity-0 group-hover:!opacity-100"
                                    icon={<LeftOutlined />}
                                    onClick={() => moveImage(image.id, "left")}
                                  />
                                </Tooltip>
                              )}

                              {/* MOVE RIGHT */}
                              {index < images.length - 1 && (
                                <Tooltip title="Move Right">
                                  <Button
                                    size="small"
                                    shape="circle"
                                    className="!absolute !top-1/2 !-translate-y-1/2 !right-1 !opacity-0 group-hover:!opacity-100"
                                    icon={<RightOutlined />}
                                    onClick={() => moveImage(image.id, "right")}
                                  />
                                </Tooltip>
                              )}

                              {/* REPLACE */}
                              <Tooltip title="Replace Image">
                                <Button
                                  type="default"
                                  shape="circle"
                                  icon={<ReloadOutlined />}
                                  size="small"
                                  className="!absolute !bottom-2 !right-2 !opacity-0 group-hover:!opacity-100"
                                  style={{ backgroundColor: "rgba(0,0,0,0.6)", color: "#fff", border: "none" }}
                                  onClick={() => triggerReplaceFile(image.id)}
                                />
                              </Tooltip>

                              {/* DELETE */}
                              <Tooltip title="Delete Image">
                                <Button
                                  type="default"
                                  shape="circle"
                                  icon={<CloseOutlined />}
                                  size="small"
                                  className="!absolute !top-2 !right-2 !opacity-0 group-hover:!opacity-100"
                                  style={{ backgroundColor: "rgba(220,38,38,0.9)", color: "#fff", border: "none" }}
                                  onClick={() => handleRemoveImage(image.id)}
                                />
                              </Tooltip>
                            </div>
                          ))}

                          <Upload accept="image/*" showUploadList={false} beforeUpload={handleImageUpload}>
                            <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer bg-white hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200">
                              <PlusOutlined className="text-2xl text-gray-400 mb-1" />
                              <Text className="text-gray-500 text-xs">Add Image</Text>
                            </div>
                          </Upload>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* STEP 1: Package Configuration */}
              {uiStep === 1 && <div className="animate-fade-in">{renderPackageConfiguration()}</div>}

              {/* STEP 2: Categories + Fabrics */}
              {uiStep === 2 && (
                <div className="animate-fade-in">
                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                    <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <ShopOutlined className="text-purple-500" />
                      Categories & Attributes
                    </Title>

                    <Row gutter={[16, 8]} className="mb-2">
                      <Col xs={24} md={12}>
                        <Form.Item label="Categories" name="categoryIds">
                          <Select {...multiSelectCommon} options={categories} labelInValue placeholder="Select categories" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="Sizes" name="sizeIds">
                          <Select {...multiSelectCommon} options={sizes} labelInValue placeholder="Select sizes" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>

                  {/* Fabrics & Colors — show only for Beds */}
                {(isBedCategory || fabrics.length > 0 || (form.getFieldValue("fabricIds") || []).length > 0) && (
                    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 mt-6">
                      <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <ShopOutlined className="text-green-500" />
                        Fabrics & Colors
                      </Title>

                      <Form.Item label="Fabrics" name="fabricIds" className="mb-6">
                        <Select
                          {...multiSelectCommon}
                          labelInValue
                          optionLabelProp="children"
                          placeholder="Select fabrics"
                          onChange={handleFabricChange}
                          tagRender={(props) => {
                            const { label, value, closable, onClose } = props;
                            const inc = fabricPrice(Number(value));
                            const cost = fabricCost(Number(value));
                            return (
                              <span className="bg-green-50 border border-green-200 px-2.5 py-1.5 rounded-lg mr-2 inline-flex items-center text-sm font-medium">
                                <span className="mr-2 text-green-700">{label}</span>
                                <span className="text-green-700 text-xs font-semibold bg-emerald-100 rounded-full px-2 py-0.5 mr-1">+€{inc.toFixed(2)}</span>
                                <span className="text-emerald-900 text-xs font-semibold bg-emerald-50 rounded-full px-2 py-0.5">Cost £{cost.toFixed(2)}</span>
                                {closable && <span onClick={onClose} className="cursor-pointer text-green-500 hover:text-green-700 font-bold ml-2">×</span>}
                              </span>
                            );
                          }}
                        >
                          {fabrics.map((fabric) => (
                            <Option key={fabric.value} value={fabric.value} label={fabric.label}>
                              <span className="flex items-center min-w-[150px]">
                                <img src={fabric.image_url} alt={fabric.label} className="w-8 h-8 mr-3 rounded-lg object-cover" />
                                <span className="mr-2">{fabric.label}</span>
                                <span className="text-green-700 text-xs font-semibold bg-emerald-100 rounded-full px-2 py-0.5 mr-1">+€{Number(fabric.price_increment ?? 0).toFixed(2)}</span>
                                <span className="text-emerald-900 text-xs font-semibold bg-emerald-50 rounded-full px-2 py-0.5">Cost £{Number(fabric.cost_increment ?? 0).toFixed(2)}</span>
                              </span>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      {/* Per-fabric color panels */}
                      <div className="space-y-4">
                        {selectedFabricsWatch.map((fab) => {
                          const fid = Number(fab.value);
                          const fabColors = colorsByFabric[fid] || [];
                          return (
                            <div key={fid} className="bg-white border border-gray-200 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <span className="text-base font-semibold text-gray-800">{fab.label}</span>
                                  <span className="text-green-700 text-xs font-semibold bg-emerald-100 rounded-full px-2 py-0.5">+€{fabricPrice(fid).toFixed(2)}</span>
                                  <span className="text-emerald-900 text-xs font-semibold bg-emerald-50 rounded-full px-2 py-0.5">Cost £{fabricCost(fid).toFixed(2)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button size="small" type="default" onClick={() => handleSelectAllColors(fid)}>
                                    Select All
                                  </Button>
                                  <Button size="small" danger type="default" onClick={() => handleClearColors(fid)}>
                                    Clear
                                  </Button>
                                </div>
                              </div>

                              {fabColors.length ? (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-3">
                                  {fabColors.map((c) => (
                                    <label key={c.value} className="inline-flex items-center gap-2 px-2 py-1">
                                      <Checkbox checked={selectedColorIdSet.has(c.value)} onChange={(e) => toggleColor(c.value, e.target.checked)} />
                                      <span className="text-gray-800">{c.label}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">No colors available for this fabric.</div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* hidden field for submission */}
                      <Form.Item name="fabricColorIds" className="hidden">
                        <Select mode="multiple" labelInValue className="hidden" />
                      </Form.Item>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 3 */}
              {uiStep === 3 && (
                <div className="animate-fade-in">
                  {renderSizePricingAndDimensions()}

                  <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                    <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <TagOutlined className="text-orange-500" />
                      Pricing & Details
                    </Title>
                   <Row gutter={[16, 8]}>
  {/* ✅ Conditionally show Cost Price only for super admin */}
  {showCostFields && (
    <Col xs={24} md={12}>
      <Form.Item label="Cost Price (£)" name="factory_price" rules={[{ required: true, message: "Please enter the Cost price!" }]}>
        <InputNumber min={0} step={0.01} className="w-full h-12 rounded-xl" placeholder="0.00" />
      </Form.Item>
    </Col>
  )}
  <Col xs={24} md={showCostFields ? 12 : 24}> {/* Adjust column span based on visibility */}
    <Form.Item label="Sale Price (€)" name="price_euro">
      <InputNumber min={0} step={0.01} className="w-full h-12 rounded-xl" placeholder="0.00" />
    </Form.Item>
  </Col>
</Row>

                    <Row gutter={[16, 8]}>
                      <Col xs={24} md={12}>
                        <Form.Item label="SKU" name="sku">
                          <Input placeholder="Enter product SKU" className="h-12 rounded-xl border-gray-300 hover:border-blue-500 focus:border-blue-500" prefix={<BarcodeOutlined className="text-gray-400" />} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="Weight (kg)" name="weight">
                          <InputNumber min={0} step={0.1} className="w-full h-12 rounded-xl" placeholder="0.0" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 8]}>
                      <Col xs={24} md={12}>
                        <Form.Item label="Status" name="status">
                          <Select className="w-full h-12 rounded-xl">
                            <Option value="in stock">In Stock</Option>
                            <Option value="out of stock">Out of Stock</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="Shipping Available" name="shippingAvailable" valuePropName="checked" className="mb-0">
                          <div className="flex items-center h-12">
                            <Switch checkedChildren="Yes" unCheckedChildren="No" className="bg-gray-300" />
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </div>
              )}

              {/* file input for replace */}
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleReplaceFile} style={{ display: "none" }} />
            </Form>

            {/* Sticky action bar */}
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 mx-2 mt-8 backdrop-blur-sm bg-white/95 action-bar-sticky">
              <div className="flex justify-between items-center">
                <Button onClick={prev} disabled={uiStep === 0} icon={<LeftOutlined />} size="large" className="rounded-lg h-12 px-6 border-gray-300 text-gray-700 hover:text-gray-900 hover:border-blue-400">
                  Previous Step
                </Button>

                {uiStep < steps.length - 1 ? (
                  <Button type="primary" onClick={next} size="large" className="rounded-lg h-12 px-8 bg-gradient-to-r from-blue-500 to-indigo-600 border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    Next Step
                  </Button>
                ) : (
                  <div className="flex gap-3">
                    <Button onClick={() => navigate("/products")} className="h-12 px-8 rounded-xl text-lg font-semibold border-gray-300 hover:border-gray-400">
                      Cancel
                    </Button>
                    <Button type="primary" onClick={() => form.submit()} loading={updating} size="large" className="rounded-lg h-12 px-8 bg-gradient-to-r from-green-500 to-emerald-600 border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                      {updating ? "Updating Product..." : "Update Product"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <style>{`
        .ant-select-selector { border-radius: 12px !important; min-height: 48px !important; }
        .ant-select-selection-item { display: flex; align-items: center; }
        .ant-input-number { border-radius: 12px !important; }
        .ant-input-number-input { height: 44px !important; }
        .ql-container { border-bottom-left-radius: 12px; border-bottom-right-radius: 12px; min-height: 180px; }
        .ql-toolbar { border-top-left-radius: 12px; border-top-right-radius: 12px; }
        .storage-types-dd .ant-select-item-option-state { display: none !important; }
        .animate-fade-in { animation: fadeIn 0.4s ease-in-out; }
        @keyframes fadeIn { from { opacity:0; transform: translateY(10px);} to { opacity:1; transform: translateY(0);} }
        .action-bar-sticky { position: sticky; bottom: 20px; z-index: 10; }
        .ant-checkbox-checked .ant-checkbox-inner { background-color: #1677ff !important; border-color: #1677ff !important; }
        .ant-checkbox:hover .ant-checkbox-inner, .ant-checkbox-input:focus + .ant-checkbox-inner { border-color: #1677ff !important; }
      `}</style>
    </div>
  );
};

export default ProductEditPage;
